import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AUTH_MODE } from "../config/cognitoConfig";
import { api } from "../services/api";
import {
  completeNewPassword,
  refreshUser,
  restoreSession,
  signIn,
  signOut,
} from "../services/cognitoAuthService";
import { ALL_DEPARTMENTS, getSelectedClientId, setSelectedClientId } from "../services/clientSelection";

const SessionContext = createContext(null);

export function useSession() {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside <SessionProvider>");
  return value;
}

/**
 * Holds who is signed in (auth), what they may do (me, from /v1/me) and which
 * client they are looking at. Pages only render once all three are known, so
 * no request is ever sent without the client header.
 */
export function SessionProvider({ children }) {
  const isDemo = AUTH_MODE !== "cognito";
  const [authUser, setAuthUser] = useState(isDemo ? { email: "demo@verainsight.com" } : null);
  const [authLoading, setAuthLoading] = useState(!isDemo);
  const [me, setMe] = useState(null);
  const [meError, setMeError] = useState(null);
  const [clientId, setClientIdState] = useState(getSelectedClientId());

  useEffect(() => {
    if (isDemo) return;
    restoreSession().then((user) => {
      setAuthUser(user);
      setAuthLoading(false);
    });
  }, [isDemo]);

  useEffect(() => {
    // No user, or a signed-up user with no role yet (the API would answer 403): nothing to load.
    if (!authUser || (!isDemo && (authUser.groups || []).length === 0)) {
      setMe(null);
      setMeError(null);
      return undefined;
    }
    let cancelled = false;
    setMeError(null);
    api
      .getMe()
      .then((data) => {
        if (cancelled) return;
        if (!data || !Array.isArray(data.availableClients)) {
          setMeError(new Error("The API did not return a valid response. Check REACT_APP_API_BASE_URL, or set REACT_APP_USE_MOCK=true."));
          return;
        }
        const stored = getSelectedClientId();
        const canSeeAll = data.staff && data.availableClients.length > 1;
        const storedIsValid =
          (stored === ALL_DEPARTMENTS && canSeeAll) ||
          data.availableClients.some((c) => String(c.id) === String(stored));
        const effective = storedIsValid
          ? String(stored)
          : String(data.client?.id ?? data.availableClients[0]?.id ?? "");
        if (effective) setSelectedClientId(effective);
        setClientIdState(effective || null);
        setMe(data);
      })
      .catch((error) => {
        if (!cancelled) setMeError(error);
      });
    return () => {
      cancelled = true;
    };
  }, [authUser, isDemo]);

  const setClientId = useCallback((id) => {
    setSelectedClientId(id);
    setClientIdState(String(id));
  }, []);

  const login = useCallback(async (email, password) => {
    const result = await signIn(email, password);
    if (result.user) setAuthUser(result.user);
    return result;
  }, []);

  const setNewPassword = useCallback(async (password) => {
    const result = await completeNewPassword(password);
    if (result.user) setAuthUser(result.user);
    return result;
  }, []);

  // For a signed-up user waiting for approval: pick up a role the admin has just granted.
  const checkApproval = useCallback(async () => {
    const user = await refreshUser();
    if (user) setAuthUser(user);
    return user;
  }, []);

  const logout = useCallback(() => {
    signOut();
    setSelectedClientId(null);
    setClientIdState(null);
    setMe(null);
    setAuthUser(null);
  }, []);

  const value = useMemo(
    () => ({
      authMode: AUTH_MODE,
      authUser,
      authLoading,
      me,
      meError,
      clientId,
      isAll: clientId === ALL_DEPARTMENTS,
      setClientId,
      login,
      setNewPassword,
      logout,
      // Signed in with Cognito but no role yet: the account exists and is waiting for an admin.
      pending: !isDemo && Boolean(authUser) && (authUser.groups || []).length === 0,
      checkApproval,
      ready: Boolean(authUser && me && clientId),
    }),
    [authUser, authLoading, me, meError, clientId, setClientId, login, setNewPassword, logout, isDemo, checkApproval]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
